import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Headphones, MessageSquare, AlertCircle, Check } from "lucide-react";
import MarkdownParser from "../MarkdownParser";
import FeedbackPanel from './FeedbackPanel';
import { sendRequest } from '../../api';

const CategoryBubble = ({ name }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/category/${name.toLowerCase().split(" ").join("-")}`);
  };

  return (
    <div
      className="inline-flex items-center px-2 py-1 mr-2 mb-1 rounded-full text-xs font-medium bg-blue-50 text-blue-600 cursor-pointer hover:bg-blue-100"
      onClick={handleClick}
    >
      {name}
    </div>
  );
};

const PublisherTag = ({ name }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/publisher/${name.toLowerCase().split(" ").join("-")}`);
  };

  return (
    <span
      className="bg-gray-200 text-gray-700 px-2 py-1 rounded text-xs mr-2 mb-2 cursor-pointer hover:bg-gray-300"
      onClick={handleClick}
    >
      {name}
    </span>
  );
};

const SummarizedText = ({ summary, onImprove }) => {
  // if (typeof summary.summarized_text === "string") {
  //   return <MarkdownParser markdown={summary.summarized_text} />
  // }

  return summary.summarized_text.map((categorySummary, index) => {
    // Get unique publishers mentioned in this category
    const publisherMentions = categorySummary.publishers || [];
    
    return (
      <section key={index} className="mb-6">
        <h3 className="text-lg font-semibold mb-2">{categorySummary.category_name}</h3>
        <div className={`markdown-slide-in stagger-${index % 5} overflow-hidden break-words pr-4`}>
          {/* Render the markdown content */}
          {categorySummary.summary_text.split("\n").map((line, lineIndex) => (
            <div key={lineIndex}>
              <MarkdownParser markdown={line} />
            </div>
          ))}
          
          {/* Add improve buttons for each publisher at the end of the section - HIDDEN */}
          {false && publisherMentions && publisherMentions.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {publisherMentions.map((publisher, pubIndex) => (
                <button 
                  key={pubIndex}
                  className="inline-flex items-center text-xs bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded"
                  onClick={() => onImprove(summary, publisher)}
                >
                  <span className="mr-1">Improve {publisher}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>
    );
  });
};

export const UserSummary = ({ userSummaries, audio, setAudio, audioDisabled = true, onViewAnnotations }) => {
  const navigate = useNavigate();
  const [generatingAudio, setGeneratingAudio] = useState({});
  const [feedbackPanelOpen, setFeedbackPanelOpen] = useState(false);
  const [activeSummary, setActiveSummary] = useState(null);
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [feedbackError, setFeedbackError] = useState({});
  const [feedbackSuccess, setFeedbackSuccess] = useState({});
  const [selectedPublisher, setSelectedPublisher] = useState(null);

  // Reset feedback success state after a timeout
  useEffect(() => {
    const successIds = Object.keys(feedbackSuccess).filter(id => feedbackSuccess[id]);
    
    if (successIds.length > 0) {
      const timers = successIds.map(id => {
        return setTimeout(() => {
          setFeedbackSuccess(prev => ({ ...prev, [id]: false }));
        }, 3000); // Reset after 3 seconds
      });
      
      return () => timers.forEach(timer => clearTimeout(timer));
    }
  }, [feedbackSuccess]);

  // useEffect(() => {
  //   if (audio?.error && audio.id) {
  //     setGeneratingAudio(prev => ({ ...prev, [audio.id]: 'error' }));
  //   }
  // }, [audio?.error]);

  const handleAudioClick = async (summary) => {
    if (!summary.has_audio) {
      try {
        setGeneratingAudio(prev => ({ ...prev, [summary.id]: true }));
        
        const response = await fetch(`${process.env.API_BASE_URL}/user_summaries/${summary.id}/generate_audio`, {
          method: 'POST',
        });
        if (!response.ok) {
          if (response.status === 403) {
            setGeneratingAudio(prev => ({ ...prev, [summary.id]: 'error' }));
            return;
          }
          throw new Error('Failed to generate podcast');
        }

        const checkAudioStatus = async () => {
          const statusResponse = await fetch(`${process.env.API_BASE_URL}/user_summaries/${summary.id}/`);
          const status = await statusResponse.json();
          
          if (status.ready) {
            setGeneratingAudio(prev => ({ ...prev, [summary.id]: false }));
            summary.has_audio = true;
          } else {
            setTimeout(checkAudioStatus, 2000);
          }
        };

        await checkAudioStatus();
        
      } catch (error) {
        console.error('Failed to generate podcast:', error);
        setGeneratingAudio(prev => ({ ...prev, [summary.id]: false }));
      }
    } else {
      setAudio({ 
        id: summary.id,
        title: summary.title, 
        url: `${process.env.AUDIO_STREAM_BASE_URL}/audio/${summary.id}` 
      });
    }
  };

  const handleAnnotationsClick = async (summaryId) => {
    if (onViewAnnotations) {
      onViewAnnotations(summaryId);
    }
  };

  const handleFeedbackClick = async (summary, publisherName = null) => {
    // Clear any previous errors
    setFeedbackError({});
    
    // If there was an error previously, just try again
    if (feedbackError[summary.id]) {
      setFeedbackError(prev => ({ ...prev, [summary.id]: null }));
    }
    
    setActiveSummary(summary);
    setSelectedPublisher(publisherName);
    setLoadingFeedback(true);
    
    try {
      // Fetch annotations data before showing the modal
      const response = await sendRequest(`/annotations/${summary.id}`);
      
      // Store the annotations data
      summary.annotations = response.annotations;
      
      // Filter annotations by publisher if a specific publisher was selected
      if (publisherName) {
        summary.annotations = summary.annotations.filter(
          annotation => annotation.summary.newsletter.publisher.name === publisherName
        );
      }
      
      // Only open the panel after data is loaded
      setFeedbackPanelOpen(true);
    } catch (error) {
      console.error('Error fetching annotations:', error);
      // Set error state with the message from the API
      setFeedbackError(prev => ({ 
        ...prev, 
        [summary.id]: error.message || 'Failed to load feedback questions'
      }));
    } finally {
      setLoadingFeedback(false);
    }
  };

  const handleSubmitFeedback = async (summaryId, answers) => {
    try {
      // In production, this would submit to the API
      // await sendRequest(`/user_summaries/${summaryId}/feedback`, 'POST', { answers });
      
      // For development, log the feedback
      console.log('Feedback submitted:', { summaryId, answers });
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Only set success state if we have answers (meaning at least one question was answered)
      if (activeSummary && Object.keys(answers).length > 0) {
        setFeedbackSuccess(prev => ({ ...prev, [activeSummary.id]: true }));
      }
      
      return true;
    } catch (error) {
      console.error('Error submitting feedback:', error);
      return false;
    }
  };

  const handleCloseFeedbackPanel = () => {
    setFeedbackPanelOpen(false);
    setActiveSummary(null);
  };

  return (
    <>
      {userSummaries.map((summary, index) => (
        <div
          key={summary.id || index}
          className="mb-2 py-6 border-b border-gray-200 summary-fade-in"
        >
          <div className="mb-4">
            <h2 className="text-xl font-bold mb-1 w-[90%] break-words">
              {summary.title}{" "}
            </h2>
            <div className="text-xs text-gray-500">
              {new Date(summary.created_at).toLocaleDateString("en-US", {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              }) + "  " + new Date(summary.created_at).toLocaleTimeString("en-US", {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
              }).toLowerCase()}
            </div>
          </div>

          <div className="mb-4">
            <div className="flex flex-wrap items-center justify-between mb-1 w-full">
              <div className="flex flex-wrap w-3/4">
                {/* {summary.publisher_names &&
                  summary.publisher_names
                    .split(" and ")
                    .map((name, idx) => (
                      <PublisherTag key={idx} name={name} />
                    ))} */}
              </div>
            </div>
            <div className="flex flex-wrap -mb-1">
              {/* {summary.category_name && <CategoryBubble name={summary.category_name} />}
              {summary.categories && summary.categories.map((category, idx) => (
                <CategoryBubble key={idx} name={category.name} />
              ))} */}
            </div>
          </div>
          <div className="flex items-center space-x-2 mr-6 pb-4">
            {!audioDisabled && <button
              className={`flex p-3 rounded-full border border-solid gap-2 transition ${
                generatingAudio[summary.id] === 'error'
                  ? "border-red-200 bg-red-50 text-red-500 cursor-not-allowed"
                  : summary.has_audio
                    ? summary.previously_listened
                      ? "border-green-200 hover:bg-green-100 text-green-500 cursor-pointer"
                      : "border-blue-200 hover:bg-blue-100 text-blue-500 cursor-pointer"
                    : generatingAudio[summary.id]
                      ? "border-gray-200 text-gray-400 cursor-not-allowed"
                      : "border-blue-200 hover:bg-blue-100 text-blue-500 cursor-pointer"
              }`}
              title={
                generatingAudio[summary.id] === 'error'
                  ? "No audio credits remaining"
                  : summary.has_audio 
                    ? "Play AI Audio" 
                    : generatingAudio[summary.id]
                      ? "Generating AI audio..." 
                      : "Generate AI Audio"
              }
              onClick={() => handleAudioClick(summary)}
              disabled={generatingAudio[summary.id]}
            >
              <Headphones size={16} />
              <div className="font-normal text-[12px]">
                {generatingAudio[summary.id] === 'error'
                  ? "No audio credits remaining"
                  : summary.has_audio 
                    ? summary.previously_listened
                      ? "Replay AI Audio"
                      : "Play AI Audio" 
                    : generatingAudio[summary.id]
                      ? "Generating audio..." 
                      : "Generate AI Audio"}
              </div>
            </button>}
          </div>
          <SummarizedText summary={summary} onImprove={() => {}} />
          
          <div className="mt-4 flex justify-between items-center">
            {summary.newsletter_id && (
              <span 
                onClick={() => window.location.href = `${process.env.API_BASE_URL}/newsletters/${summary.newsletter_id}` }
                className="text-blue-500 hover:text-blue-600 text-xs font-medium cursor-pointer"
              >
                Read more →
              </span>
            )}
            
            {/* HIDDEN - Improve feedback button */}
            {false && <span 
              onClick={() => handleFeedbackClick(summary)}
              className={`${feedbackError[summary.id] 
                ? "text-red-500 hover:text-red-600" 
                : feedbackSuccess[summary.id]
                  ? "text-green-500 hover:text-green-600"
                  : "text-purple-500 hover:text-purple-600"} 
                text-xs font-medium cursor-pointer flex items-center 
                ${!feedbackError[summary.id] && !feedbackSuccess[summary.id] ? "pulse-animation" : ""} 
                rounded-full px-2 py-1`}
              title={feedbackError[summary.id] || (feedbackSuccess[summary.id] ? "Saved" : "Provide feedback to improve summaries")}
            >
              {loadingFeedback && activeSummary?.id === summary.id ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-purple-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading...
                </span>
              ) : feedbackError[summary.id] ? (
                <span className="flex items-center">
                  <AlertCircle size={14} className="mr-1" />
                  <span className="mr-1">Error</span>
                  <span className="ml-1">→</span>
                </span>
              ) : feedbackSuccess[summary.id] ? (
                <span className="flex items-center animate-check-in">
                  <Check size={14} className="mr-1" />
                  <span className="mr-1">Saved</span>
                </span>
              ) : (
                <>
                  <span className="mr-1">Improve</span>
                  <span className="ml-1">→</span>
                </>
              )}
            </span>}
          </div>
        </div>
      ))}

      {/* Feedback Panel - HIDDEN */}
      {false && activeSummary && (
        <FeedbackPanel
          isOpen={feedbackPanelOpen}
          onClose={handleCloseFeedbackPanel}
          summaryId={activeSummary.id}
          summaryTitle={activeSummary.title}
          annotations={activeSummary.annotations}
          onSubmitFeedback={handleSubmitFeedback}
          selectedPublisher={selectedPublisher}
        />
      )}
    </>
  );
};

export default UserSummary;
